import { z } from "zod";
import { currencySchema, idSchema, languageTagSchema } from "./common.ts";

const restaurantFields = {
  name: z.string().min(1).max(120),
  logoAssetId: idSchema.nullable().optional(),
  address: z.string().max(300).optional(),
  phone: z.string().max(40).optional(),
  website: z.string().url().max(300).optional().or(z.literal("")),
  defaultLanguage: languageTagSchema,
  currency: currencySchema,
  timezone: z.string().min(1).max(64),
};

export const createRestaurantSchema = z.object({
  ...restaurantFields,
  defaultLanguage: restaurantFields.defaultLanguage.default("en"),
  currency: restaurantFields.currency.default("EUR"),
  timezone: restaurantFields.timezone.default("Europe/Sarajevo"),
});
export type CreateRestaurantInput = z.infer<typeof createRestaurantSchema>;

// Built from the default-free fields: Zod 4 applies `.default()` even inside `.partial()`, which
// would silently reset language/currency/timezone on every PATCH.
export const updateRestaurantSchema = z.object(restaurantFields).partial().extend({
  taxRateBps: z.number().int().min(0).max(10_000).optional(),
  taxInclusive: z.boolean().optional(),
  orderingEnabled: z.boolean().optional(),
  orderingPaused: z.boolean().optional(),
  minimumOrderMinor: z.number().int().min(0).max(10_000_000).optional(),
  customDomain: z
    .string()
    .regex(/^(?=.{4,253}$)([a-z0-9-]{1,63}\.)+[a-z]{2,63}$/, "Enter a domain like menu.example.com")
    .nullable()
    .optional(),
  domainMenuId: idSchema.nullable().optional(),
});

export type RestaurantDto = {
  id: string;
  organizationId: string;
  name: string;
  slug: string;
  logoAssetId: string | null;
  logoUrl: string | null;
  address: string | null;
  phone: string | null;
  website: string | null;
  defaultLanguage: string;
  currency: string;
  timezone: string;
  taxRateBps: number;
  taxInclusive: boolean;
  orderingEnabled: boolean;
  orderingPaused: boolean;
  minimumOrderMinor: number;
  customDomain: string | null;
  domainMenuId: string | null;
  createdAt: string;
};

export type DomainCheckDto = {
  domain: string;
  /** Hostname a CNAME should point to, and the server addresses for apex A records. */
  target: string;
  targetIps: string[];
  dns: { status: "ok" | "missing" | "wrong"; found: string[] };
  https: "ok" | "pending";
  url: string;
};

export const createTableSchema = z.object({
  label: z.string().min(1).max(40),
});
export const bulkCreateTablesSchema = z.object({
  prefix: z.string().max(20).default("Table "),
  from: z.number().int().min(0).max(9999),
  to: z.number().int().min(0).max(9999),
});
export const updateTableSchema = z.object({
  label: z.string().min(1).max(40).optional(),
  isActive: z.boolean().optional(),
});

export type TableDto = { id: string; restaurantId: string; label: string; qrToken: string; isActive: boolean; sortOrder: number; url: string };

export const qrOptionsSchema = z.object({
  format: z.enum(["svg", "png"]).default("svg"),
  size: z.coerce.number().int().min(128).max(2048).default(512),
  fg: z.string().regex(/^[0-9a-fA-F]{6}$/).optional(),
  bg: z.string().regex(/^[0-9a-fA-F]{6}$/).optional(),
  margin: z.coerce.number().int().min(0).max(8).default(2),
  ecc: z.enum(["L", "M", "Q", "H"]).default("M"),
  source: z.string().max(40).regex(/^[a-z0-9_-]*$/).optional(),
});
